import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { EntityId } from '../../core/models/domain.models';
import { InMemoryDataService, MoveStudentCourseResponse } from '../../core/data/in-memory-data.service';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { EnterSubmitDirective } from '../../core/directives/enter-submit.directive';

export interface MoveStudentCourseOption {
  id: EntityId;
  label: string;
}

export interface MoveStudentCourseDialogData {
  studentId: EntityId;
  sourceCourseInstanceId: EntityId;
  sourceCourseName: string;
  targetOptions: MoveStudentCourseOption[];
}

@Component({
  selector: 'app-move-student-course-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatProgressBarModule,
    MatSelectModule,
    TranslatePipe
    ,EnterSubmitDirective
  ],
  template: `
    <h2 mat-dialog-title>{{ 'studentCourse.move.title' | t }}</h2>
    <form appEnterSubmit [formGroup]="form" (ngSubmit)="move()">
    <mat-dialog-content>
      <div class="move-summary">
        <span>{{ 'studentCourse.move.from' | t }}</span>
        <strong>{{ data.sourceCourseName }}</strong>
      </div>

      @if (data.targetOptions.length > 0) {
        <div class="move-form">
          <mat-form-field>
            <mat-label>{{ 'studentCourse.move.to' | t }}</mat-label>
            <mat-select formControlName="targetCourseInstanceId">
              @for (option of data.targetOptions; track option.id) {
                <mat-option [value]="option.id">{{ option.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-checkbox formControlName="moveLessons">{{ 'studentCourse.move.moveLessons' | t }}</mat-checkbox>
          <mat-checkbox formControlName="movePayments">{{ 'studentCourse.move.movePayments' | t }}</mat-checkbox>
        </div>
      } @else {
        <p class="empty-message">{{ 'studentCourse.move.noTargets' | t }}</p>
      }

      @if (moving()) {
        <mat-progress-bar mode="indeterminate" />
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" [disabled]="moving()" (click)="dialogRef.close(null)">{{ 'common.cancel' | t }}</button>
      <button mat-flat-button type="submit" color="primary" [disabled]="form.invalid || moving() || data.targetOptions.length === 0">
        {{ 'studentCourse.move.confirm' | t }}
      </button>
    </mat-dialog-actions>
    </form>
  `,
  styles: `
    form { display: contents; }
    .move-summary,
    .move-form {
      display: grid;
      gap: 12px;
      min-width: 320px;
    }

    .move-summary {
      margin-bottom: 12px;
    }

    .move-summary span {
      color: #64748b;
      font-size: 0.78rem;
      font-weight: 800;
      text-transform: uppercase;
    }

    .empty-message {
      margin: 0;
      color: #64748b;
    }

    mat-progress-bar {
      margin-top: 12px;
    }
  `
})
export class MoveStudentCourseDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly dataService = inject(InMemoryDataService);
  readonly data = inject<MoveStudentCourseDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject<MatDialogRef<MoveStudentCourseDialogComponent, MoveStudentCourseResponse | null>>(MatDialogRef);
  readonly moving = signal(false);
  readonly form = this.fb.group({
    targetCourseInstanceId: [this.data.targetOptions[0]?.id ?? null, Validators.required],
    moveLessons: [true],
    movePayments: [true]
  });

  async move(): Promise<void> {
    if (this.form.invalid || this.moving()) {
      return;
    }
    const value = this.form.getRawValue();
    const targetCourseInstanceId = value.targetCourseInstanceId;
    if (targetCourseInstanceId == null) {
      return;
    }

    this.moving.set(true);
    try {
      const response = await this.dataService.moveStudentCourseInstance(
        this.data.studentId,
        this.data.sourceCourseInstanceId,
        {
          targetCourseInstanceId,
          status: 'IN_PROGRESS',
          moveLessons: value.moveLessons !== false,
          movePayments: value.movePayments !== false
        }
      );
      this.dialogRef.close(response);
    } catch {
      this.moving.set(false);
    }
  }
}
